import { getMergedFilterParams, getMergedFilterQueryString } from 'core/queryString'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { postService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as Hooks from '../../../../utils/hooks'
import { getPostStatusKey, getPostStatusLabel } from '../../../../utils/post'
import EditTable from 'components/organisms/EditTable'
import SvgIcon from 'components/atoms/SvgIcon'

import { Button, Pagination, CheckBox, Panel } from '../../../atoms'

import { FlashMessage } from '../../../organisms'
import TextClamp from 'components/atoms/TextClamp'
import Typography from 'components/atoms/_layouts/Typography'
import SidebarLayout from '../../_layouts/sidebarLayout'
import { SearchHeader } from 'components/organisms/Posts/SearchHeader'

interface IWindow {
  flashMessages: FlashMessage
  location: any
}

declare var window: IWindow

interface IProps {
  posts: {
    posts: IJsonResponse
  }
  tags: IJsonResponse
  languages: IJsonResponse
  categories: IJsonResponse
  areas: IJsonResponse
  user: IJsonResponse
}

// DMCツアーはshowページを持たないため、一覧からのリンクは専用の編集画面にする
const postEditPath = record =>
  record.dmc_tour_id
    ? `/agent/dmc_tours/${record.dmc_tour_id}/edit`
    : `/agent/posts/${record.slug}/edit`

const COLUMNS = [
  {
    name: I18n.t('generic.status'),
    field: 'aasm_state_i18n',
    renderElement: record => {
      return (
        <>
          {record.has_reservations && (
            <div className="Post_reservation_status_mobile">
              <Typography type="subtitle" size="lg" color="secondary">
                {I18n.t('post.has_reservation')}
              </Typography>
            </div>
          )}
          <div className={`Post_status ${getPostStatusKey(record)}`}>
            {getPostStatusLabel(record)}
          </div>
        </>
      )
    },
  },
  {
    name: '',
    field: 'post_image',
    renderElement: record => (
      <div className="Post_image">
        <a href={record.dmc_tour_id ? postEditPath(record) : `/posts/${record.slug}`}>
          <img
            src={record.post_image?.thumb_url ?? `${constants.ASSET_HOST}/images/no-image.png`}
            loading="lazy"
            alt={`${record.name} - ${record.short_name}`}
          />
        </a>
      </div>
    ),
  },
  {
    name: I18n.t('post.post_name'),
    field: 'name',
    renderElement: record => (
      <div className="Post_name_place">
        <span className="Post_name">
          <a href={postEditPath(record)}>
            <TextClamp line={2} width={200}>
              {record.name}
            </TextClamp>
          </a>
        </span>
        <span className="Post_meeting_point_name">
          <SvgIcon name="place" className="icon" ariaHidden={true} role="presentation" />
          {record.meeting_point_name}
        </span>
      </div>
    ),
  },
  {
    name: I18n.t('post.short_name'),
    field: 'short_name',
  },
  {
    name: I18n.t('post.family_owner'),
    field: 'family_owner',
    renderElement: record => {
      return record.family_owner ? (
        <div className="Post_family_owner">
          <SvgIcon name="check" className="icon" ariaHidden={true} role="presentation" />
        </div>
      ) : null
    },
  },
]

const FILTER_FIELDS = [
  'unpublished',
  'published',
  'dmc',
  'has_reservations',
  'date',
  'min_price',
  'max_price',
  'guests',
  'category_ids',
  'area_ids',
  'tag_ids',
  'language_ids',
  'post_types',
]

const PostIndex: React.FC<IProps> = props => {
  const { posts: initialPosts, pagination: initialPagination } = postService.getPostsFromJson(
    props.posts.posts
  )
  const [posts, setPosts] = React.useState(initialPosts)
  const [pagination, setPagination] = React.useState(initialPagination)
  const urlParams = new URLSearchParams(window.location.search)
  const { data: user } = postService.getDataFromJson(props.user)

  Hooks.SetDocumentTitle(I18n.t('post.registered_posts_index'))

  const onChangePageHandler = page => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', page)

    location.href = `${location.pathname}?${params.toString()}`
  }

  const searchOwnPost = async baseParams => {
    const keywords = urlParams.get('keywords')
    const params = { ...baseParams }

    if (keywords) {
      params.keywords = keywords
    }

    return postService.searchOwnPost(params)
  }

  const handleOnFilterSubmit = React.useCallback(
    async filterValue => {
      const filterParams = getMergedFilterParams(FILTER_FIELDS, filterValue)
      const filterQueryString = getMergedFilterQueryString(FILTER_FIELDS, filterParams)
      const result = await searchOwnPost({ ...filterParams })

      setPosts(result.posts)
      setPagination(result.pagination)
      history.replaceState(null, null, `?${filterQueryString}`)
    },
    [posts]
  )

  const handlePublishPost = React.useCallback(
    async record => {
      const { post_path } = await postService.publish(record)
      window.flashMessages.addMessage({ text: 'Post Published', type: 'success' })
      location.href = `/agent/posts`
    },
    [posts]
  )

  const handleShowReservationsPage = () => {
    location.href = `/agent/host_reservations`
  }

  const handleDeletePost = React.useCallback(async record => {
    if (!confirm(I18n.t('generic.delete_confirmation'))) {
      return
    }
    try {
      const { flush } = await postService.delete(record.id)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = `${location.pathname}${location.search}`
    } catch (e) {
      console.error(e)
    }
  }, [])

  const renderRecordControls = React.useCallback(
    record => (
      <>
        <Button
          key="edit"
          textColor={constants.COLORS.subText}
          borderColor={constants.COLORS.subText}
          small={true}
          rounded={true}
        >
          <a href={postEditPath(record)}>{I18n.t('generic.edit')}</a>
        </Button>
        {!record.dmc_tour_id && record.aasm_state !== 'published' && !record.has_reservations && (
          <Button
            key="delete"
            handleClick={() => handleDeletePost(record)}
            textColor={constants.COLORS.caution}
            borderColor={constants.COLORS.caution}
            small={true}
            rounded={true}
          >
            {I18n.t('generic.delete')}
          </Button>
        )}
        {record.aasm_state === 'published' && record.has_reservations && (
          <Button
            key="suspend"
            handleClick={() => handleShowReservationsPage()}
            textColor={constants.COLORS.success}
            borderColor={constants.COLORS.success}
            small={true}
            rounded={true}
          >
            {I18n.t('post.reservation_confirmation')}
          </Button>
        )}
        {!record.dmc_tour_id && record.aasm_state === 'suspended' && (
          <Button
            key="publish"
            handleClick={() => handlePublishPost(record)}
            disabled={record.aasm_state !== 'suspended'}
            textColor={constants.COLORS.success}
            borderColor={constants.COLORS.success}
            small={true}
            rounded={true}
          >
            {I18n.t('generic.publish')}
          </Button>
        )}
      </>
    ),
    []
  )

  const showUnpublishedTours = event => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    if (event.target.checked) {
      currentUrlParams.set('unpublished', 'true')
    } else {
      currentUrlParams.delete('unpublished')
    }
    currentUrlParams.set('page', '1')

    location.href = `posts?${currentUrlParams.toString()}`
  }

  const getShowUnPublishedTours = () => {
    const newurlParams = new URLSearchParams(window.location.search)
    return newurlParams.get('unpublished') === 'true'
  }

  const showPublishedTours = event => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    if (event.target.checked) {
      currentUrlParams.set('published', 'true')
    } else {
      currentUrlParams.set('published', 'false')
    }
    currentUrlParams.set('page', '1')

    location.href = `posts?${currentUrlParams.toString()}`
  }

  const getShowPublishedTours = () => {
    const newurlParams = new URLSearchParams(window.location.search)
    return newurlParams.get('published') !== 'false'
  }

  // DMCツアーは公開/非公開のチェックに関わらず、このチェック時のみ表示する
  const showDmcTours = event => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    if (event.target.checked) {
      currentUrlParams.set('dmc', 'true')
    } else {
      currentUrlParams.delete('dmc')
    }
    currentUrlParams.set('page', '1')

    location.href = `posts?${currentUrlParams.toString()}`
  }

  const getShowDmcTours = () => {
    const newurlParams = new URLSearchParams(window.location.search)
    return newurlParams.get('dmc') === 'true'
  }

  const showHasReservationsTours = event => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    if (event.target.checked) {
      currentUrlParams.set('has_reservations', 'true')
    } else {
      currentUrlParams.delete('has_reservations')
    }
    currentUrlParams.set('page', '1')

    location.href = `posts?${currentUrlParams.toString()}`
  }

  const getShowHasReservationsTours = () => {
    const newurlParams = new URLSearchParams(window.location.search)
    return newurlParams.get('has_reservations') === 'true'
  }

  return (
    <SidebarLayout user={user}>
      <PostsIndexWrapper>
        <Panel
          marginType="dashboard"
          title={
            <div className="PostIndex_Title">
              <div>{I18n.t('post.registered_posts_index')}</div>
              {((user.guide && user.guide_approved) || user.agent) && (
                <Button
                  key="new"
                  textColor={constants.COLORS.primary}
                  borderColor={constants.COLORS.primary}
                  small={true}
                >
                  <a href={`/agent/posts/new`}>
                    <SvgIcon name="add" className="icon" ariaHidden={true} role="presentation" />
                    {I18n.t('post.new')}
                  </a>
                </Button>
              )}
              {user.agent && (
                <Button
                  key="new_dmc"
                  textColor={constants.COLORS.primary}
                  borderColor={constants.COLORS.primary}
                  small={true}
                >
                  <a href={`/agent/dmc_tours/new`}>
                    <SvgIcon name="add" className="icon" ariaHidden={true} role="presentation" />
                    {I18n.t('dmc_tour.new')}
                  </a>
                </Button>
              )}
            </div>
          }
        >
          <div className="PostIndex_Header">
            <div className="PostIndex_Filters">
              <div>
                <CheckBox
                  name="unpublished_posts"
                  label={I18n.t('post.unpublished')}
                  defaultChecked={getShowUnPublishedTours()}
                  onChangeHandler={showUnpublishedTours}
                  showLabelInline={true}
                />
              </div>
              <div>
                <CheckBox
                  name="published_posts"
                  label={I18n.t('post.published')}
                  defaultChecked={getShowPublishedTours()}
                  onChangeHandler={showPublishedTours}
                  showLabelInline={true}
                />
              </div>
              <div>
                <CheckBox
                  name="dmc_posts"
                  label={I18n.t('post.dmc')}
                  defaultChecked={getShowDmcTours()}
                  onChangeHandler={showDmcTours}
                  showLabelInline={true}
                />
              </div>
              <div>
                <CheckBox
                  name="has_reservations"
                  label={I18n.t('post.has_reservation')}
                  defaultChecked={getShowHasReservationsTours()}
                  onChangeHandler={showHasReservationsTours}
                  showLabelInline={true}
                />
              </div>
            </div>
            <SearchHeader
              handleOnFilterSubmit={handleOnFilterSubmit}
              categories={props.categories}
              areas={props.areas}
              tags={props.tags}
              languages={props.languages}
            />
          </div>

          {posts.length === 0 ? (
            <div className="PostIndex_NoPost">
              <SvgIcon
                name="calendar_today"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
              <h1>{I18n.t('post.no_posts')}</h1>
            </div>
          ) : (
            <EditTable
              editable={true}
              sortable={true}
              sortableColumns={['name']}
              columns={COLUMNS}
              records={posts}
              pagination={null}
              renderRecordControls={renderRecordControls}
            />
          )}

          {pagination && (
            <Pagination
              onChangePageHandler={onChangePageHandler}
              currentPage={pagination.current_page}
              prevPage={pagination.prev_page}
              nextPage={pagination.next_page}
              totalCount={pagination.total_count}
              totalPages={pagination.total_pages}
            />
          )}
        </Panel>
      </PostsIndexWrapper>
    </SidebarLayout>
  )
}
const PostsIndexWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding: 20px 0;
  }

  .PostIndex_Title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    > div:first-child {
      flex: 1;
    }
    .icon {
      width: 16px;
      height: 16px;
    }
  }

  .PostIndex_NoPost {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-top: 24px;
    > .icon {
      width: 32px;
      height: 32px;
    }
    > h1 {
      margin-top: 12px;
      font-weight: bold;
      font-size: 20px;
    }
  }

  .Post_status {
    height: 24px;
    border-radius: 30px;
    padding: 3px 20px;
    text-align: center;
    width: fit-content;
    color: ${constants.COLORS.useallyText};
    background: ${constants.COLORS.useallyBG};
    &.published {
      color: ${constants.COLORS.successText};
      background: ${constants.COLORS.successBG};
    }
    &.limited_published,
    &.dmc {
      color: ${constants.COLORS.info2};
      background: ${constants.COLORS.infoBG2};
    }
    &.suspended {
      color: ${constants.COLORS.subText};
      background: ${constants.COLORS.lightGray};
    }
  }

  .Post_image {
    img {
      width: 70px;
      height: 70px;
      border-radius: 10px;
      object-fit: cover;
    }
  }

  .Post_price {
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    color: #333333;
  }

  .Post_dates {
    min-width: 120px;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 18px;
    color: #333333;
  }

  .Post_reservation_status {
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      display: none;
    }
    .Post_has_reservations {
      font-weight: bold;
      font-size: 12px;
      line-height: 18px;
      color: ${constants.COLORS.secondary};
    }
    .Post_no_reservation {
      font-style: normal;
      font-weight: bold;
      font-size: 12px;
      line-height: 18px;
      color: ${constants.COLORS.divider};
    }
  }
  .Post_reservation_status_mobile {
    display: none;
    text-align: center;
    width: 100%;
    padding: 8px;
    background: ${constants.COLORS.successBG};
    margin-bottom: 24px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      display: block;
    }
  }
  .Post_name_place {
    display: flex;
    flex-direction: column;
    width: 200px;
    .Post_name {
      a {
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 21px;
        color: #333333;
      }
    }
    .Post_meeting_point_name {
      display: flex;
      align-items: center;
      font-style: normal;
      font-weight: normal;
      font-size: 10px;
      line-height: 15px;
      color: #33808c;
      .icon {
        width: 16px;
        height: 16px;
      }
    }
  }

  .Post_family_owner {
    display: flex;
    align-items: center;
    justify-content: center;
    .icon {
      width: 20px;
      height: 20px;
      color: ${constants.COLORS.primary};
    }
  }
  .PostIndex_Filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 1em 0;
  }
`

export default PostIndex
